/**
 * Full ISO 3166-1 alpha-3 country list with English display names.
 *
 * Used by:
 *   - Nationality picker (any country, including non-launch)
 *   - Grew-up-in picker (same)
 *   - Country-of-residence picker (restricted to LAUNCH_COUNTRIES_ISO3
 *     because pricing is country-locked at signup — see
 *     plan §Currency & Pricing)
 *
 * Source: ISO 3166-1 alpha-3 (2024). Iran is included because users
 * have nationality / heritage there even though the platform is not
 * offered IN Iran (OFAC). Marketing + operations gate on country of
 * residence, not nationality.
 */

export interface CountryEntry {
  iso3: string;
  name: string;
}

export const COUNTRIES_ALL: readonly CountryEntry[] = [
  { iso3: 'AFG', name: 'Afghanistan' },
  { iso3: 'ALB', name: 'Albania' },
  { iso3: 'DZA', name: 'Algeria' },
  { iso3: 'AND', name: 'Andorra' },
  { iso3: 'AGO', name: 'Angola' },
  { iso3: 'ATG', name: 'Antigua and Barbuda' },
  { iso3: 'ARG', name: 'Argentina' },
  { iso3: 'ARM', name: 'Armenia' },
  { iso3: 'AUS', name: 'Australia' },
  { iso3: 'AUT', name: 'Austria' },
  { iso3: 'AZE', name: 'Azerbaijan' },
  { iso3: 'BHS', name: 'Bahamas' },
  { iso3: 'BHR', name: 'Bahrain' },
  { iso3: 'BGD', name: 'Bangladesh' },
  { iso3: 'BRB', name: 'Barbados' },
  { iso3: 'BLR', name: 'Belarus' },
  { iso3: 'BEL', name: 'Belgium' },
  { iso3: 'BLZ', name: 'Belize' },
  { iso3: 'BEN', name: 'Benin' },
  { iso3: 'BTN', name: 'Bhutan' },
  { iso3: 'BOL', name: 'Bolivia' },
  { iso3: 'BIH', name: 'Bosnia and Herzegovina' },
  { iso3: 'BWA', name: 'Botswana' },
  { iso3: 'BRA', name: 'Brazil' },
  { iso3: 'BRN', name: 'Brunei' },
  { iso3: 'BGR', name: 'Bulgaria' },
  { iso3: 'BFA', name: 'Burkina Faso' },
  { iso3: 'BDI', name: 'Burundi' },
  { iso3: 'CPV', name: 'Cabo Verde' },
  { iso3: 'KHM', name: 'Cambodia' },
  { iso3: 'CMR', name: 'Cameroon' },
  { iso3: 'CAN', name: 'Canada' },
  { iso3: 'CAF', name: 'Central African Republic' },
  { iso3: 'TCD', name: 'Chad' },
  { iso3: 'CHL', name: 'Chile' },
  { iso3: 'CHN', name: 'China' },
  { iso3: 'COL', name: 'Colombia' },
  { iso3: 'COM', name: 'Comoros' },
  { iso3: 'COG', name: 'Congo' },
  { iso3: 'COD', name: 'Congo (Democratic Republic)' },
  { iso3: 'CRI', name: 'Costa Rica' },
  { iso3: 'CIV', name: "Côte d'Ivoire" },
  { iso3: 'HRV', name: 'Croatia' },
  { iso3: 'CUB', name: 'Cuba' },
  { iso3: 'CYP', name: 'Cyprus' },
  { iso3: 'CZE', name: 'Czechia' },
  { iso3: 'DNK', name: 'Denmark' },
  { iso3: 'DJI', name: 'Djibouti' },
  { iso3: 'DMA', name: 'Dominica' },
  { iso3: 'DOM', name: 'Dominican Republic' },
  { iso3: 'ECU', name: 'Ecuador' },
  { iso3: 'EGY', name: 'Egypt' },
  { iso3: 'SLV', name: 'El Salvador' },
  { iso3: 'GNQ', name: 'Equatorial Guinea' },
  { iso3: 'ERI', name: 'Eritrea' },
  { iso3: 'EST', name: 'Estonia' },
  { iso3: 'SWZ', name: 'Eswatini' },
  { iso3: 'ETH', name: 'Ethiopia' },
  { iso3: 'FJI', name: 'Fiji' },
  { iso3: 'FIN', name: 'Finland' },
  { iso3: 'FRA', name: 'France' },
  { iso3: 'GAB', name: 'Gabon' },
  { iso3: 'GMB', name: 'Gambia' },
  { iso3: 'GEO', name: 'Georgia' },
  { iso3: 'DEU', name: 'Germany' },
  { iso3: 'GHA', name: 'Ghana' },
  { iso3: 'GRC', name: 'Greece' },
  { iso3: 'GRD', name: 'Grenada' },
  { iso3: 'GTM', name: 'Guatemala' },
  { iso3: 'GIN', name: 'Guinea' },
  { iso3: 'GNB', name: 'Guinea-Bissau' },
  { iso3: 'GUY', name: 'Guyana' },
  { iso3: 'HTI', name: 'Haiti' },
  { iso3: 'HND', name: 'Honduras' },
  { iso3: 'HKG', name: 'Hong Kong' },
  { iso3: 'HUN', name: 'Hungary' },
  { iso3: 'ISL', name: 'Iceland' },
  { iso3: 'IND', name: 'India' },
  { iso3: 'IDN', name: 'Indonesia' },
  { iso3: 'IRN', name: 'Iran' },
  { iso3: 'IRQ', name: 'Iraq' },
  { iso3: 'IRL', name: 'Ireland' },
  { iso3: 'ISR', name: 'Israel' },
  { iso3: 'ITA', name: 'Italy' },
  { iso3: 'JAM', name: 'Jamaica' },
  { iso3: 'JPN', name: 'Japan' },
  { iso3: 'JOR', name: 'Jordan' },
  { iso3: 'KAZ', name: 'Kazakhstan' },
  { iso3: 'KEN', name: 'Kenya' },
  { iso3: 'KIR', name: 'Kiribati' },
  { iso3: 'XKX', name: 'Kosovo' },
  { iso3: 'KWT', name: 'Kuwait' },
  { iso3: 'KGZ', name: 'Kyrgyzstan' },
  { iso3: 'LAO', name: 'Laos' },
  { iso3: 'LVA', name: 'Latvia' },
  { iso3: 'LBN', name: 'Lebanon' },
  { iso3: 'LSO', name: 'Lesotho' },
  { iso3: 'LBR', name: 'Liberia' },
  { iso3: 'LBY', name: 'Libya' },
  { iso3: 'LIE', name: 'Liechtenstein' },
  { iso3: 'LTU', name: 'Lithuania' },
  { iso3: 'LUX', name: 'Luxembourg' },
  { iso3: 'MAC', name: 'Macao' },
  { iso3: 'MDG', name: 'Madagascar' },
  { iso3: 'MWI', name: 'Malawi' },
  { iso3: 'MYS', name: 'Malaysia' },
  { iso3: 'MDV', name: 'Maldives' },
  { iso3: 'MLI', name: 'Mali' },
  { iso3: 'MLT', name: 'Malta' },
  { iso3: 'MHL', name: 'Marshall Islands' },
  { iso3: 'MRT', name: 'Mauritania' },
  { iso3: 'MUS', name: 'Mauritius' },
  { iso3: 'MEX', name: 'Mexico' },
  { iso3: 'FSM', name: 'Micronesia' },
  { iso3: 'MDA', name: 'Moldova' },
  { iso3: 'MCO', name: 'Monaco' },
  { iso3: 'MNG', name: 'Mongolia' },
  { iso3: 'MNE', name: 'Montenegro' },
  { iso3: 'MAR', name: 'Morocco' },
  { iso3: 'MOZ', name: 'Mozambique' },
  { iso3: 'MMR', name: 'Myanmar' },
  { iso3: 'NAM', name: 'Namibia' },
  { iso3: 'NRU', name: 'Nauru' },
  { iso3: 'NPL', name: 'Nepal' },
  { iso3: 'NLD', name: 'Netherlands' },
  { iso3: 'NZL', name: 'New Zealand' },
  { iso3: 'NIC', name: 'Nicaragua' },
  { iso3: 'NER', name: 'Niger' },
  { iso3: 'NGA', name: 'Nigeria' },
  { iso3: 'PRK', name: 'North Korea' },
  { iso3: 'MKD', name: 'North Macedonia' },
  { iso3: 'NOR', name: 'Norway' },
  { iso3: 'OMN', name: 'Oman' },
  { iso3: 'PAK', name: 'Pakistan' },
  { iso3: 'PLW', name: 'Palau' },
  { iso3: 'PSE', name: 'Palestine' },
  { iso3: 'PAN', name: 'Panama' },
  { iso3: 'PNG', name: 'Papua New Guinea' },
  { iso3: 'PRY', name: 'Paraguay' },
  { iso3: 'PER', name: 'Peru' },
  { iso3: 'PHL', name: 'Philippines' },
  { iso3: 'POL', name: 'Poland' },
  { iso3: 'PRT', name: 'Portugal' },
  { iso3: 'QAT', name: 'Qatar' },
  { iso3: 'ROU', name: 'Romania' },
  { iso3: 'RUS', name: 'Russia' },
  { iso3: 'RWA', name: 'Rwanda' },
  { iso3: 'KNA', name: 'Saint Kitts and Nevis' },
  { iso3: 'LCA', name: 'Saint Lucia' },
  { iso3: 'VCT', name: 'Saint Vincent and the Grenadines' },
  { iso3: 'WSM', name: 'Samoa' },
  { iso3: 'SMR', name: 'San Marino' },
  { iso3: 'STP', name: 'São Tomé and Príncipe' },
  { iso3: 'SAU', name: 'Saudi Arabia' },
  { iso3: 'SEN', name: 'Senegal' },
  { iso3: 'SRB', name: 'Serbia' },
  { iso3: 'SYC', name: 'Seychelles' },
  { iso3: 'SLE', name: 'Sierra Leone' },
  { iso3: 'SGP', name: 'Singapore' },
  { iso3: 'SVK', name: 'Slovakia' },
  { iso3: 'SVN', name: 'Slovenia' },
  { iso3: 'SLB', name: 'Solomon Islands' },
  { iso3: 'SOM', name: 'Somalia' },
  { iso3: 'ZAF', name: 'South Africa' },
  { iso3: 'KOR', name: 'South Korea' },
  { iso3: 'SSD', name: 'South Sudan' },
  { iso3: 'ESP', name: 'Spain' },
  { iso3: 'LKA', name: 'Sri Lanka' },
  { iso3: 'SDN', name: 'Sudan' },
  { iso3: 'SUR', name: 'Suriname' },
  { iso3: 'SWE', name: 'Sweden' },
  { iso3: 'CHE', name: 'Switzerland' },
  { iso3: 'SYR', name: 'Syria' },
  { iso3: 'TWN', name: 'Taiwan' },
  { iso3: 'TJK', name: 'Tajikistan' },
  { iso3: 'TZA', name: 'Tanzania' },
  { iso3: 'THA', name: 'Thailand' },
  { iso3: 'TLS', name: 'Timor-Leste' },
  { iso3: 'TGO', name: 'Togo' },
  { iso3: 'TON', name: 'Tonga' },
  { iso3: 'TTO', name: 'Trinidad and Tobago' },
  { iso3: 'TUN', name: 'Tunisia' },
  { iso3: 'TUR', name: 'Türkiye' },
  { iso3: 'TKM', name: 'Turkmenistan' },
  { iso3: 'TUV', name: 'Tuvalu' },
  { iso3: 'UGA', name: 'Uganda' },
  { iso3: 'UKR', name: 'Ukraine' },
  { iso3: 'ARE', name: 'United Arab Emirates' },
  { iso3: 'GBR', name: 'United Kingdom' },
  { iso3: 'USA', name: 'United States' },
  { iso3: 'URY', name: 'Uruguay' },
  { iso3: 'UZB', name: 'Uzbekistan' },
  { iso3: 'VUT', name: 'Vanuatu' },
  { iso3: 'VAT', name: 'Vatican City' },
  { iso3: 'VEN', name: 'Venezuela' },
  { iso3: 'VNM', name: 'Vietnam' },
  { iso3: 'YEM', name: 'Yemen' },
  { iso3: 'ZMB', name: 'Zambia' },
  { iso3: 'ZWE', name: 'Zimbabwe' },
] as const;

/** O(1) lookup by ISO-3 code. */
export const COUNTRY_BY_ISO3: ReadonlyMap<string, CountryEntry> = new Map(
  COUNTRIES_ALL.map((c) => [c.iso3, c]),
);

/** Display name for an ISO-3 code, falls back to the code itself. */
export function countryName(iso3: string | null | undefined): string {
  if (!iso3) return '';
  return COUNTRY_BY_ISO3.get(iso3)?.name ?? iso3;
}
